import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, HeartHandshake, CreditCard, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

const err = (e) => { const d = e?.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(' ') : d || 'Something went wrong'; };

const GiftRow = ({ g, token, onChanged }) => {
  const { toast } = useToast();
  const [amt, setAmt] = useState(String(g.amount));
  const [busy, setBusy] = useState('');
  const save = async () => {
    setBusy('amt');
    try { const r = await api.post(`/donations/monthly/${g.id}/amount`, { token, amount: parseFloat(amt) }); toast({ title: r.data.message }); onChanged(); }
    catch (e) { toast({ title: 'Could not update', description: err(e) }); } finally { setBusy(''); }
  };
  const portal = async () => {
    setBusy('portal');
    try { const r = await api.post(`/donations/monthly/${g.id}/portal`, { token, origin_url: window.location.origin }); window.location.href = r.data.url; }
    catch (e) { toast({ title: 'Unavailable', description: err(e) }); setBusy(''); }
  };
  return (
    <div className="bg-white/80 rounded-2xl border border-rose/40 p-5" data-testid={`monthly-gift-${g.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><div className="text-burgundy font-medium">{g.fund}</div><div className="text-xs text-mahogany/60">${Number(g.amount).toFixed(2)} every month · {g.status}</div></div>
        <button onClick={portal} disabled={!!busy} className="pill-btn-outline text-sm" data-testid={`monthly-portal-${g.id}`}>{busy === 'portal' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Update card or cancel</button>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-mahogany/70">New monthly amount $</span>
        <input type="number" min="5" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-28 bg-white rounded-xl border border-rose/50 px-3 py-1.5 text-sm" data-testid={`monthly-amount-${g.id}`} />
        <button onClick={save} disabled={!!busy || !(parseFloat(amt) >= 5) || parseFloat(amt) === g.amount} className="pill-btn text-sm" data-testid={`monthly-amount-save-${g.id}`}>{busy === 'amt' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update amount</button>
      </div>
    </div>
  );
};

const RequestLink = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post('/donations/monthly/manage-link', { email, origin_url: window.location.origin }); toast({ title: r.data.message }); }
    catch (e2) { toast({ title: 'Failed', description: err(e2) }); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="bg-white/80 rounded-2xl border border-rose/40 p-5 flex gap-2 flex-wrap" data-testid="monthly-request-link">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email you give with" className="flex-1 min-w-[200px] bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm" data-testid="monthly-request-email" />
      <button disabled={busy} className="pill-btn text-sm" data-testid="monthly-request-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me a secure link</button>
    </form>
  );
};

export default function ManageGiving() {
  const [params] = useSearchParams();
  const token = params.get('t');
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/donations/monthly/mine', { params: token ? { t: token } : {} }).then((r) => setData(r.data)).catch((e) => setData({ error: err(e) })), [token]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="App" data-testid="page-manage-giving">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[760px] mx-auto px-6 py-16">
          <div className="eyebrow mb-3 inline-flex items-center gap-2"><HeartHandshake className="w-4 h-4 text-rose-italic" /> Monthly giving</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-burgundy">Manage your <span className="font-italic-display text-rose-italic">monthly gift</span></h1>
          <div className="mt-8 space-y-4">
            {data === null ? <Loader2 className="w-6 h-6 animate-spin text-mahogany" />
              : data.error ? (<><p className="text-sm text-mahogany/75" data-testid="monthly-error">{data.error}</p><RequestLink /></>)
              : data.gifts.length === 0 ? (<><p className="text-sm text-mahogany/75" data-testid="monthly-none">No active monthly gifts for {data.email}. Gave with a different email?</p><RequestLink /></>)
              : data.gifts.map((g) => <GiftRow key={g.id} g={g} token={token} onChanged={load} />)}
          </div>
          <p className="mt-6 text-xs text-mahogany/60">Every monthly gift gets its own tax receipt by email and is included in your year-end giving statement. Meditative Mindfulness Inc · EIN 92-1474053.</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
